import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

const SIGNER_COLORS = [
  '#4173FF', '#FF6B6B', '#00C49F', '#FFBB28',
  '#A855F7', '#F97316', '#14B8A6', '#EC4899',
];

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  return { r, g, b };
}

/**
 * Genera una copia del PDF original con los recuadros de firma dibujados
 * calcando de forma exacta el estilo y colores del visor del editor.
 * 
 * @param {string} rawBase64Pdf - El PDF original codificado en Base64.
 * @param {Array} signers - Listado de firmantes con sus respectivas coordenadas y metadatos.
 * @returns {Promise<string>} - El nuevo PDF generado codificado en Base64.
 */
export async function generateReferencePdf(rawBase64Pdf, signers) {
  // Cargar el PDF original
  const pdfDoc = await PDFDocument.load(rawBase64Pdf);
  const pages = pdfDoc.getPages();

  // Registrar fuentes estándar de PDF
  const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  // Obtener el listado de correos únicos en orden de aparición para calcular el mismo color index que el editor
  const uniqueEmails = [];
  for (const signer of signers) {
    if (signer.email && !uniqueEmails.includes(signer.email)) {
      uniqueEmails.push(signer.email);
    }
  }

  for (const signer of signers) {
    // Si la firma no ha sido colocada, la omitimos
    if (signer.pos_x === null || signer.pos_x === undefined) {
      continue;
    }

    const pageIndex = (signer.page || 1) - 1;
    if (pageIndex < 0 || pageIndex >= pages.length) {
      continue;
    }

    const page = pages[pageIndex];
    const { height: pageHeight } = page.getSize();

    const w = signer.width || 140;
    const h = signer.height || 80;
    const rot = signer.rotation || 0;

    // Calcular el centro del recuadro
    const cx = signer.pos_x + w / 2;
    const cy = signer.pos_y + h / 2;

    const bboxW = (rot === 90 || rot === 270) ? h : w;
    const bboxH = (rot === 90 || rot === 270) ? w : h;

    const bboxX = Math.round(cx - bboxW / 2);
    const bboxY = Math.round(cy - bboxH / 2);

    const pdfX = bboxX;
    const pdfY = pageHeight - bboxY - bboxH;

    // Obtener el color del firmante de forma idéntica a como lo hace el editor (basado en el índice de lista)
    const idx = uniqueEmails.indexOf(signer.email);
    const colorIndex = idx >= 0 ? idx % SIGNER_COLORS.length : 0;
    const color = hexToRgb(SIGNER_COLORS[colorIndex]);

    // 1. Dibujar el fondo del recuadro redondeado usando un path SVG (con esquinas redondeadas hacia afuera)
    const r = 8; // Radio de redondeo
    // Definimos el path relativo en el eje estándar. Al pasarlo a pdf-lib con Y invertido, 
    // colocamos y: pdfY + bboxH (tope izquierdo) y usamos sweep-flag: 1 para curvar hacia afuera.
    const roundedRectPath = `M ${r},0 h ${bboxW - 2 * r} a ${r},${r} 0 0 1 ${r},${r} v ${bboxH - 2 * r} a ${r},${r} 0 0 1 -${r},${r} h -${bboxW - 2 * r} a ${r},${r} 0 0 1 -${r},-${r} v -${bboxH - 2 * r} a ${r},${r} 0 0 1 ${r},-${r} z`;

    page.drawSvgPath(roundedRectPath, {
      x: pdfX,
      y: pdfY + bboxH, // Origen en el tope izquierdo de la caja
      color: rgb(1, 1, 1),
      borderColor: rgb(0.88, 0.88, 0.88), // Borde gris claro
      borderWidth: 1.2,
    });

    // 2. Dibujar el nombre, correo electrónico y leyenda del firmante
    const textX = pdfX + 12;
    const nameFontSize = Math.min(bboxH * 0.16, 9);
    const emailFontSize = Math.min(bboxH * 0.12, 7.5);
    const legendFontSize = Math.min(bboxH * 0.12, 7);

    const availableTextWidth = bboxW - 24; // 12pt de margen de seguridad a los lados

    // Estimar límite de caracteres aproximado (Helvetica Bold ~ 58% de font size, Helvetica ~ 45%)
    const maxNameChars = Math.floor(availableTextWidth / (nameFontSize * 0.58));
    const maxEmailChars = Math.floor(availableTextWidth / (emailFontSize * 0.45));

    let nameText = signer.name || 'Firmante';
    if (nameText.length > maxNameChars) {
      nameText = nameText.substring(0, Math.max(5, maxNameChars - 3)) + '...';
    }

    page.drawText(nameText, {
      x: textX,
      y: pdfY + bboxH * 0.68,
      size: nameFontSize,
      font: helveticaBold,
      color: rgb(color.r, color.g, color.b),
    });

    let emailText = signer.email || '';
    if (emailText.length > maxEmailChars) {
      emailText = emailText.substring(0, Math.max(5, maxEmailChars - 3)) + '...';
    }

    page.drawText(emailText, {
      x: textX,
      y: pdfY + bboxH * 0.45,
      size: emailFontSize,
      font: helveticaFont,
      color: rgb(0.45, 0.45, 0.45),
    });

    const legendText = "Su firma irá aquí";
    page.drawText(legendText, {
      x: textX,
      y: pdfY + bboxH * 0.22,
      size: legendFontSize,
      font: helveticaFont,
      color: rgb(color.r, color.g, color.b),
    });
  }

  // Guardar el PDF y retornar como string Base64
  const pdfBytes = await pdfDoc.save();

  let binary = '';
  const len = pdfBytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(pdfBytes[i]);
  }
  return window.btoa(binary);
}
